import { useEffect, useState } from "react";
import { api, euro, formatDateTime } from "@/lib/api";
import { HandCoins, CheckCircle2, Copy, ExternalLink, FileText, Download, Pencil, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Donations({ isAdmin }) {
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState({ total_confirmed: 0, donors_count: 0 });
  const [paypal, setPaypal] = useState("");
  const [tab, setTab] = useState("all");
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const cfg = await api.get("/config");
    setPaypal(cfg.data.paypal_me || "");
    api.get("/donations/stats").then(r => setStats(r.data)).catch(() => {});
    if (isAdmin) {
      const r = await api.get("/donations");
      setDonations(r.data);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const confirm = async (id) => {
    if (!window.confirm("Confermare la donazione? Dopo comparirà in Ricevute → Da emettere, dove potrai emettere la ricevuta.")) return;
    await api.post(`/donations/${id}/confirm`);
    toast.success("Donazione confermata. Ora emettila da Ricevute → Da emettere."); load();
  };
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({});
  const [pp, setPp] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const loadPp = () => api.get("/admin/paypal/status").then(r => setPp(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) loadPp(); }, [isAdmin]);
  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/paypal/sync?days=14");
      toast.success(`PayPal: ${data.seen} pagamenti letti, ${data.imported} nuovi importati`);
      load(); loadPp();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore sincronizzazione PayPal"); }
    finally { setSyncing(false); }
  };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/donations/${id}`, { ...edit, amount: parseFloat(edit.amount), donor_codice_fiscale: edit.donor_codice_fiscale || null });
      toast.success("Dati aggiornati"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa donazione? (Solo se non confermata)")) return;
    try { await api.delete(`/donations/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

  const emptyManual = { donor_name: "", donor_email: "", donor_codice_fiscale: "", amount: "", method: "contanti", date: new Date().toISOString().slice(0, 10), note: "" };
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState(emptyManual);
  const [savingManual, setSavingManual] = useState(false);
  const addManual = async (e) => {
    e.preventDefault();
    setSavingManual(true);
    try {
      await api.post("/donations/manual", {
        ...manual,
        amount: parseFloat(manual.amount),
        donor_email: manual.donor_email || null,
        donor_codice_fiscale: manual.donor_codice_fiscale || null,
        note: manual.note || null,
      });
      toast.success("Donazione registrata. Ora è tra quelle da confermare.");
      setShowManual(false); setManual(emptyManual); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSavingManual(false); }
  };

  const filtered = donations.filter(d => tab === "all" || (tab === "pending" ? !d.confirmed : d.confirmed));
  const pending = donations.filter(d => !d.confirmed).length;

  return (
    <div data-testid="section-donations">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><HandCoins className="w-3 h-3" /> Sostegno</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Donazioni</h1>
        <p className="text-[#6B6558] mt-2">Sostieni l&apos;Associazione con una libera donazione o quota tessera.</p>
      </div>

      <div className={`grid gap-4 mb-6 ${isAdmin ? "md:grid-cols-3" : "sm:grid-cols-2"}`}>
        <div className="paper rounded-2xl p-5" data-testid="stat-total-confirmed">
          <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Totale confermato</div>
          <div className="font-display text-3xl font-bold text-[#16A34A] mt-2">{euro(stats.total_confirmed)}</div>
        </div>
        <div className="paper rounded-2xl p-5" data-testid="stat-donors">
          <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Donatori</div>
          <div className="font-display text-3xl font-bold text-[#2B3A33] mt-2">{stats.donors_count}</div>
        </div>
        {isAdmin && (
          <div className="paper rounded-2xl p-5" data-testid="stat-pending">
            <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">In attesa conferma</div>
            <div className="font-display text-3xl font-bold text-[#D97706] mt-2">{pending}</div>
          </div>
        )}
      </div>

      <div className="paper rounded-2xl p-6 mb-6" style={{background: "linear-gradient(135deg, #FFF7ED, #FFFFFF)"}}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="font-display text-xl font-bold text-[#2B3A33]">Il tuo link PayPal</div>
            <div className="text-sm text-[#6B6558] mt-1">Condividi questo link con chiunque voglia donare.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="px-3 py-2 rounded-lg bg-white border border-[#E8E2D8] text-xs font-mono">{paypal || "Non configurato"}</code>
            {paypal && (
              <>
                <button data-testid="copy-paypal" onClick={() => copy(paypal)} className="px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4] text-sm inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copia</button>
                <a data-testid="open-paypal" href={paypal} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg text-sm font-semibold text-white inline-flex items-center gap-1" style={{background:"#0070BA"}}><ExternalLink className="w-3 h-3" /> Apri</a>
              </>
            )}
          </div>
        </div>
      </div>

      {isAdmin && pp && (
        <div className="paper rounded-2xl p-5 mb-6" data-testid="paypal-webhook-status">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="font-display text-lg font-bold text-[#2B3A33]">Collegamento automatico PayPal</div>
              <div className="text-xs text-[#6B6558] mt-1">Ogni pagamento ricevuto compare qui "Da confermare": il Tesoriere verifica, corregge i dati se serve e poi invia la ricevuta.</div>
            </div>
            <div className="flex gap-2 text-xs font-semibold flex-wrap">
              <span className={`px-2 py-1 rounded-full ${pp.credentials_ok ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>Credenziali {pp.credentials_ok ? "OK" : "KO"}</span>
              <span className={`px-2 py-1 rounded-full ${pp.webhook_id_set ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]"}`}>Webhook {pp.webhook_id_set ? "attivo" : "ID mancante"}</span>
              <span className={`px-2 py-1 rounded-full ${pp.transaction_search ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`} data-testid="paypal-txsearch-badge">Transaction Search {pp.transaction_search ? "OK" : "disattivo"}</span>
              <span className="px-2 py-1 rounded-full bg-[#F3EEE4] text-[#2B3A33] uppercase">{pp.env}</span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button data-testid="paypal-sync-btn" disabled={syncing} onClick={syncNow} className="px-4 py-2 rounded-xl bg-[#2B3A33] text-white text-xs font-semibold disabled:opacity-60">{syncing ? "Sincronizzo…" : "Sincronizza ora da PayPal"}</button>
            {pp.last_sync && <span className="text-xs text-[#6B6558]">Ultima sincronizzazione: {new Date(pp.last_sync).toLocaleString("it-IT")} · {pp.last_sync_imported ?? 0} nuove</span>}
          </div>
          {pp.transaction_search === false && (
            <div className="mt-3 text-xs bg-[#FEF3C7] border-l-4 border-[#D97706] text-[#78350F] rounded-lg p-3">
              I pagamenti da link paypal.me / pulsante Dona <b>non generano webhook</b>: serve il <b>Transaction Search</b>. Su developer.paypal.com → Apps & Credentials → <b>Live</b> → la tua app → sezione <b>Features</b> → spunta <b>Transaction Search</b> → Save. Poi attendi ~10 minuti e premi "Sincronizza ora".
            </div>
          )}
          {!pp.webhook_id_set && (
            <div className="mt-3 text-xs text-[#6B6558]">URL da inserire su developer.paypal.com → App → Webhooks: <code className="bg-white border border-[#E8E2D8] rounded px-2 py-0.5">{pp.webhook_url}</code></div>
          )}
        </div>
      )}

      {isAdmin && (
        <>
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            {["all","pending","confirmed"].map(t => (
              <button key={t} data-testid={`don-tab-${t}`} onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t ? "bg-[#2B3A33] text-white" : "bg-white border border-[#E8E2D8] text-[#2B3A33]"}`}>
                {t === "all" ? "Tutte" : t === "pending" ? "Da confermare" : "Confermate"}
              </button>
            ))}
            <div className="flex-1" />
            <button data-testid="add-manual-donation" onClick={() => setShowManual(v => !v)}
              className="px-4 py-2 rounded-full text-sm font-semibold terracotta-btn inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Aggiungi donazione manuale
            </button>
            <a data-testid="export-csv" href={`${backend}/api/donations/export.csv?fiscal_year=${new Date().getFullYear()}`}
              className="px-4 py-2 rounded-full text-sm font-semibold border border-[#E8E2D8] bg-white hover:bg-[#F3EEE4] inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Export CSV {new Date().getFullYear()}
            </a>
          </div>

          {showManual && (
            <div className="paper rounded-2xl p-6 mb-4" data-testid="manual-donation-form">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-lg font-bold text-[#2B3A33]">Registra una donazione</div>
                <button onClick={() => setShowManual(false)} className="p-1 rounded-lg hover:bg-[#F3EEE4]" data-testid="close-manual"><X className="w-4 h-4" /></button>
              </div>
              <form onSubmit={addManual} className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Donatore *</label>
                  <input data-testid="manual-name" required value={manual.donor_name} onChange={e => setManual(v => ({...v, donor_name: e.target.value}))}
                    placeholder="Nome e cognome" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Importo (€) *</label>
                  <input data-testid="manual-amount" required type="number" step="0.01" min="0.01" value={manual.amount} onChange={e => setManual(v => ({...v, amount: e.target.value}))}
                    placeholder="0,00" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Metodo *</label>
                  <select data-testid="manual-method" value={manual.method} onChange={e => setManual(v => ({...v, method: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm bg-white">
                    <option value="contanti">Contanti</option>
                    <option value="bonifico">Bonifico bancario</option>
                    <option value="paypal">PayPal</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Data</label>
                  <input data-testid="manual-date" type="date" value={manual.date} onChange={e => setManual(v => ({...v, date: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Email (facoltativa)</label>
                  <input data-testid="manual-email" type="email" value={manual.donor_email} onChange={e => setManual(v => ({...v, donor_email: e.target.value}))}
                    placeholder="per invio ricevuta" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Codice Fiscale (facoltativo)</label>
                  <input data-testid="manual-cf" maxLength={16} value={manual.donor_codice_fiscale} onChange={e => setManual(v => ({...v, donor_codice_fiscale: e.target.value.toUpperCase()}))}
                    placeholder="per ricevuta detraibile" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm font-mono" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-[#6B6558] uppercase tracking-wide">Nota (facoltativa)</label>
                  <input data-testid="manual-note" value={manual.note} onChange={e => setManual(v => ({...v, note: e.target.value}))}
                    placeholder="es. donazione evento, causale bonifico…" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-2 flex-wrap">
                  <button data-testid="manual-submit" disabled={savingManual} className="px-5 py-2.5 rounded-xl terracotta-btn font-semibold text-sm disabled:opacity-60">
                    {savingManual ? "Salvo…" : "Salva donazione"}
                  </button>
                  <button type="button" onClick={() => setShowManual(false)} className="px-5 py-2.5 rounded-xl border border-[#E8E2D8] text-sm font-semibold">Annulla</button>
                  <span className="text-xs text-[#6B6558]">Verrà aggiunta come "Da confermare": poi potrai emettere la ricevuta.</span>
                </div>
              </form>
            </div>
          )}

          <div className="paper rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#8A8272] border-b border-[#E8E2D8]">
                  <th className="p-4">Data</th>
                  <th className="p-4">Donatore</th>
                  <th className="p-4 hidden sm:table-cell">Metodo</th>
                  <th className="p-4 text-right">Importo</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan="5" className="p-8 text-center text-[#6B6558]">Nessuna donazione.</td></tr>
                )}
                {filtered.map(d => (
                  <tr key={d.id} data-testid={`donation-${d.id}`} className="border-b border-[#E8E2D8]/60">
                    <td className="p-4 text-[#6B6558] text-xs">{formatDateTime(d.created_at)}</td>
                    <td className="p-4">
                      <div className="font-medium text-[#2B3A33]">{d.donor_name}</div>
                      {d.donor_email && <div className="text-xs text-[#6B6558]">{d.donor_email}</div>}
                    </td>
                    <td className="p-4 hidden sm:table-cell text-[#6B6558] uppercase text-xs">{d.method}</td>
                    <td className="p-4 text-right font-mono font-bold text-[#16A34A]">{euro(d.amount)}</td>
                    <td className="p-4 text-right">
                      {d.confirmed ? (
                        <div className="inline-flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs text-[#16A34A] font-semibold"><CheckCircle2 className="w-4 h-4" /> Confermata</span>
                          {d.receipt_id ? (
                            <a data-testid={`receipt-${d.id}`} href={`${backend}/api/receipts/${d.receipt_id}/pdf`} target="_blank" rel="noreferrer"
                              className="text-xs px-2 py-1 rounded border border-[#E8E2D8] hover:bg-[#FDF2F2] inline-flex items-center gap-1">
                              <FileText className="w-3 h-3" /> Ricevuta {d.receipt_number}
                            </a>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded bg-[#FEF3C7] text-[#92400E] font-semibold">Ricevuta da emettere</span>
                          )}
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                          {editing === d.id ? (
                            <form onSubmit={e => { e.preventDefault(); saveEdit(d.id); }} className="flex flex-wrap gap-1 justify-end" data-testid={`edit-form-${d.id}`}>
                              <input value={edit.donor_name} onChange={e => setEdit(v => ({...v, donor_name: e.target.value}))} placeholder="Nome" className="px-2 py-1 rounded border border-[#E8E2D8] text-xs w-32" />
                              <input value={edit.donor_email} onChange={e => setEdit(v => ({...v, donor_email: e.target.value}))} placeholder="Email" className="px-2 py-1 rounded border border-[#E8E2D8] text-xs w-40" />
                              <input value={edit.donor_codice_fiscale} onChange={e => setEdit(v => ({...v, donor_codice_fiscale: e.target.value.toUpperCase()}))} placeholder="Codice Fiscale" maxLength={16} className="px-2 py-1 rounded border border-[#E8E2D8] text-xs w-36 font-mono" />
                              <input type="number" step="0.01" value={edit.amount} onChange={e => setEdit(v => ({...v, amount: e.target.value}))} className="px-2 py-1 rounded border border-[#E8E2D8] text-xs w-20" />
                              <button className="text-xs px-2 py-1 rounded bg-[#2B3A33] text-white" data-testid={`save-${d.id}`}>Salva</button>
                              <button type="button" onClick={() => setEditing(null)} className="text-xs px-2 py-1 rounded border border-[#E8E2D8]">Annulla</button>
                            </form>
                          ) : (
                            <>
                              {d.source === "paypal_webhook" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] font-semibold" title={d.tx_reference}>PayPal auto</span>}
                              <button data-testid={`edit-${d.id}`} onClick={() => { setEditing(d.id); setEdit({ donor_name: d.donor_name || "", donor_email: d.donor_email || "", donor_codice_fiscale: d.donor_codice_fiscale || "", amount: d.amount }); }} className="text-xs px-2 py-1.5 rounded-lg border border-[#E8E2D8] hover:bg-[#F3EEE4]" title="Modifica dati prima della ricevuta"><Pencil className="w-3 h-3" /></button>
                              <button data-testid={`delete-${d.id}`} onClick={() => remove(d.id)} className="text-xs px-2 py-1.5 rounded-lg border border-[#E8E2D8] hover:bg-[#FEE2E2] text-[#991B1B]" title="Elimina (es. duplicato o non è una donazione)"><Trash2 className="w-3 h-3" /></button>
                              <button data-testid={`confirm-${d.id}`} onClick={() => confirm(d.id)} className="text-xs px-3 py-1.5 rounded-lg terracotta-btn font-semibold">Conferma donazione</button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
